import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Sección opcional para introducir varios tramos de IVA (21% + 10% + 4%...).
 * Se activa cuando el usuario pulsa "+ Añadir varios tramos", momento en el que
 * `breakdown` pasa a ser un array con al menos un elemento.
 * El padre recalcula subtotal/tax/amount cuando la lista tiene ≥ 2 tramos.
 */
export default function MultiIvaSection({ breakdown, onChange, lang }) {
  const active = Array.isArray(breakdown) && breakdown.length > 0;

  const addBracket = () => {
    const next = active ? [...breakdown] : [
      { _uid: crypto.randomUUID(), base: "", rate: "21", tax: "" },
    ];
    next.push({ _uid: crypto.randomUUID(), base: "", rate: "10", tax: "" });
    onChange(next);
  };

  const updateBracket = (idx, patch) => {
    const next = [...breakdown];
    next[idx] = { ...next[idx], ...patch };
    // Auto-calcular IVA cuando base y % están presentes y no se toca la casilla IVA.
    const baseN = parseFloat(next[idx].base);
    const rateN = parseFloat(next[idx].rate);
    if (isFinite(baseN) && isFinite(rateN) && patch.tax === undefined) {
      next[idx].tax = (baseN * rateN / 100).toFixed(2);
    }
    onChange(next);
  };

  const removeBracket = (idx) => {
    const next = breakdown.filter((_, i) => i !== idx);
    onChange(next.length >= 2 ? next : null);
  };

  const clearAll = () => onChange(null);

  if (!active) {
    return (
      <div className="border-t border-dashed border-[var(--border-color)] pt-3 mt-1">
        <button
          type="button"
          onClick={addBracket}
          data-testid="multi-iva-toggle"
          className="text-xs text-[var(--primary)] hover:underline font-medium"
        >
          + {lang === "es" ? "Añadir varios tramos de IVA (21% + 10% + 4%...)" : "Add multiple VAT brackets"}
        </button>
      </div>
    );
  }

  const sumBase = breakdown.reduce((a, b) => a + (parseFloat(b.base) || 0), 0);
  const sumTax = breakdown.reduce((a, b) => a + (parseFloat(b.tax) || 0), 0);
  const sumTotal = sumBase + sumTax;

  return (
    <div className="border-t border-dashed border-[var(--border-color)] pt-3 mt-1 space-y-2" data-testid="multi-iva-section">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
          {lang === "es" ? "Tramos de IVA" : "VAT brackets"}
        </Label>
        <button
          type="button"
          onClick={clearAll}
          className="text-xs text-[var(--status-negative)] hover:underline"
          data-testid="multi-iva-clear"
        >
          {lang === "es" ? "Quitar tramos" : "Clear brackets"}
        </button>
      </div>
      <div className="space-y-1.5">
        {breakdown.map((b, idx) => (
          <div key={b._uid || idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`multi-iva-row-${idx}`}>
            <div className="col-span-4">
              <Input
                type="number"
                step="0.01"
                value={b.base}
                onChange={(e) => updateBracket(idx, { base: e.target.value })}
                placeholder={lang === "es" ? "Base" : "Net"}
                className="h-9 font-mono-num"
                data-testid={`multi-iva-base-${idx}`}
              />
            </div>
            <div className="col-span-2">
              <Input
                type="number"
                step="0.1"
                value={b.rate}
                onChange={(e) => updateBracket(idx, { rate: e.target.value })}
                placeholder="%"
                className="h-9 font-mono-num"
                data-testid={`multi-iva-rate-${idx}`}
              />
            </div>
            <div className="col-span-4">
              <Input
                type="number"
                step="0.01"
                value={b.tax}
                onChange={(e) => updateBracket(idx, { tax: e.target.value })}
                placeholder={lang === "es" ? "IVA" : "VAT"}
                className="h-9 font-mono-num"
                data-testid={`multi-iva-tax-${idx}`}
              />
            </div>
            <button
              type="button"
              onClick={() => removeBracket(idx)}
              className="col-span-2 p-2 rounded hover:bg-[var(--status-negative-bg)] text-[var(--status-negative)] text-xs"
              data-testid={`multi-iva-remove-${idx}`}
            >
              <X size={14} className="inline" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addBracket}
          className="text-xs text-[var(--primary)] hover:underline"
          data-testid="multi-iva-add"
        >
          + {lang === "es" ? "Añadir tramo" : "Add bracket"}
        </button>
      </div>
      <div className="text-xs text-[var(--text-secondary)] flex items-center gap-4 pt-2 border-t border-[var(--border-color)]">
        <span>{lang === "es" ? "Suma bases:" : "Sum nets:"} <span className="font-mono-num font-semibold">{sumBase.toFixed(2)}</span></span>
        <span>{lang === "es" ? "Suma IVA:" : "Sum VAT:"} <span className="font-mono-num font-semibold">{sumTax.toFixed(2)}</span></span>
        <span>{lang === "es" ? "Total:" : "Total:"} <span className="font-mono-num font-semibold">{sumTotal.toFixed(2)}</span></span>
      </div>
    </div>
  );
}
